// src/services/users.ts
import api from "./api";
import {
  User,
  UserFilters,
  CreateUserRequest,
  UpdateUserRequest,
  UpdateUserRolesRequest,
  UpdateUserStatusRequest,
  TransferUserRequest,
  ResetPasswordRequest,
  UserStatistics,
} from "../types/user";
import { PaginatedResponse, ApiResponse } from "../types/common";

const USERS_ENDPOINT = "/users";

export const userService = {
  // Get all users with pagination and filtering
  async getUsers(filters: UserFilters = {}): Promise<PaginatedResponse<User>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    try {
      const data: any = await api.get(`${USERS_ENDPOINT}?${params.toString()}`);

      return {
        success: data.success !== false,
        data: data.users || [],
        pagination: {
          currentPage: data.currentPage || 1,
          totalPages: data.totalPages || 1,
          totalItems: data.total || 0,
          hasNext: (data.currentPage || 1) < (data.totalPages || 1),
          hasPrev: (data.currentPage || 1) > 1,
        },
      };
    } catch (error: any) {
      console.error("Error fetching users:", error.message || error);
      throw error;
    }
  },

  // Get a single user by ID
  async getUser(id: string): Promise<ApiResponse<User>> {
    try {
      const data: any = await api.get(`${USERS_ENDPOINT}/${id}`);
      return data;
    } catch (error) {
      console.error("Error fetching user:", error);
      throw error;
    }
  },

  // Create a new user
  async createUser(userData: CreateUserRequest): Promise<ApiResponse<User>> {
    try {
      const data: any = await api.post("/auth/register", userData);
      return data;
    } catch (error) {
      console.error("Error creating user:", error);
      throw error;
    }
  },

  // Update user profile information
  async updateUser(
    id: string,
    userData: UpdateUserRequest
  ): Promise<ApiResponse<User>> {
    try {
      const data: any = await api.put(`${USERS_ENDPOINT}/${id}`, userData);
      return data;
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  },

  // Update user roles
  async updateUserRoles(
    id: string,
    roleData: UpdateUserRolesRequest
  ): Promise<ApiResponse<User>> {
    try {
      const data: any = await api.put(
        `${USERS_ENDPOINT}/${id}/roles`,
        roleData
      );
      return data;
    } catch (error) {
      console.error("Error updating user roles:", error);
      throw error;
    }
  },

  // Update user status (activate/deactivate/suspend)
  async updateUserStatus(
    id: string,
    statusData: UpdateUserStatusRequest
  ): Promise<ApiResponse<User>> {
    try {
      // Use the dedicated status endpoint
      const data: any = await api.put(
        `${USERS_ENDPOINT}/${id}/status`,
        statusData
      );
      return data;
    } catch (error) {
      console.error("Error updating user status:", error);
      throw error;
    }
  },

  // Transfer user to another branch
  async transferUser(
    id: string,
    transferData: TransferUserRequest
  ): Promise<ApiResponse<User>> {
    try {
      const data: any = await api.put(
        `${USERS_ENDPOINT}/${id}/transfer`,
        transferData
      );
      return data;
    } catch (error) {
      console.error("Error transferring user:", error);
      throw error;
    }
  },

  // Reset user password
  async resetUserPassword(
    id: string,
    passwordData: ResetPasswordRequest
  ): Promise<ApiResponse<{ message: string }>> {
    try {
      const data: any = await api.put(
        `${USERS_ENDPOINT}/${id}/reset-password`,
        passwordData
      );
      return data;
    } catch (error) {
      console.error("Error resetting user password:", error);
      throw error;
    }
  },

  // Delete user (soft delete)
  async deleteUser(id: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const data: any = await api.delete(`${USERS_ENDPOINT}/${id}`);
      return data;
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    }
  },

  // Get user statistics
  async getUserStatistics(): Promise<ApiResponse<UserStatistics>> {
    try {
      const data: any = await api.get(`${USERS_ENDPOINT}/statistics`);
      return data;
    } catch (error) {
      console.error("Error fetching user statistics:", error);
      throw error;
    }
  },

  // Bulk operations
  async bulkUpdateUsers(
    userIds: string[],
    updates: Partial<UpdateUserRequest>
  ): Promise<ApiResponse<{ modifiedCount: number }>> {
    try {
      const data: any = await api.put(`${USERS_ENDPOINT}/bulk`, {
        userIds,
        updates,
      });
      return data;
    } catch (error) {
      console.error("Error bulk updating users:", error);
      throw error;
    }
  },

  // Export users data
  async exportUsers(filters: UserFilters = {}): Promise<Blob> {
    try {
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, String(value));
        }
      });

      const data: any = await api.get(
        `${USERS_ENDPOINT}/export?${params.toString()}`,
        {
          responseType: "blob",
        }
      );
      return data;
    } catch (error) {
      console.error("Error exporting users:", error);
      throw error;
    }
  },

  // Assign branches to a user
  async assignBranches(
    userId: string,
    branchIds: string[]
  ): Promise<ApiResponse<User>> {
    try {
      const data: any = await api.post(`${USERS_ENDPOINT}/${userId}/branches`, {
        branchIds,
      });
      return data;
    } catch (error) {
      console.error("Error assigning branches:", error);
      throw error;
    }
  },

  // Remove a branch from a user
  async removeBranch(
    userId: string,
    branchId: string
  ): Promise<ApiResponse<User>> {
    try {
      const data: any = await api.delete(
        `${USERS_ENDPOINT}/${userId}/branches/${branchId}`
      );
      return data;
    } catch (error) {
      console.error("Error removing branch:", error);
      throw error;
    }
  },

  // Get user's assigned branches
  async getUserBranches(userId: string): Promise<
    ApiResponse<{
      branches: Array<{
        _id: string;
        name: string;
        code: string;
        address?: string;
        phone?: string;
        status: string;
      }>;
      primaryBranch: string;
    }>
  > {
    try {
      const data: any = await api.get(`${USERS_ENDPOINT}/${userId}/branches`);
      return data;
    } catch (error) {
      console.error("Error getting user branches:", error);
      throw error;
    }
  },
};
